/**
 * 场地上画 TA 用哪张图、怎么对位。优先级：
 * 1. 这只宠物的贪吃动作帧（播放器此刻的那一帧；按实测落地锚点对准宠物在场地上的位置）；
 * 2. 星球形象的中性站姿（验过透明通道的才用；按脚底锚点站在宠物位置上）；
 * 3. 物种插画；
 * 4. 都没有：画一个圆点小脸（draw.ts 兜底）。
 * 游戏里不镜像：不对称的花纹、耳朵不能靠翻转得到另一侧。
 */
import type { CharacterState, PetSpecies } from "@/shared/contracts";
import type { MotionFrame, MotionSet } from "@/features/pet_motion";
import { speciesArtUrl } from "@/shared/ui";

export interface SpriteSpec {
  url: string;
  /** 整张图的像素尺寸 */
  width: number;
  height: number;
  /** 主体高度（像素）：按它和宠物半径定比例 */
  contentHeight: number;
  /** 对准宠物位置的那个点（像素） */
  anchorX: number;
  anchorY: number;
  /** 对准点相对宠物中心往下挪多少个半径：俯视的动作帧锚在身体投影中心（0），正面立绘锚在脚底（往下挪一点才像站在那个位置） */
  groundOffset: number;
  source: "motion" | "character" | "species";
}

/** 画出来的主体高度 ＝ 宠物直径 × 这个倍数 */
export const SPRITE_HEIGHT_PER_DIAMETER = 1.7;

export function spriteFromFrame(frame: MotionFrame, canvas: MotionSet["canvas"]): SpriteSpec {
  return {
    url: frame.url,
    width: canvas.width,
    height: canvas.height,
    contentHeight: frame.content_box.bottom - frame.content_box.top,
    anchorX: frame.anchor.x * canvas.width,
    anchorY: frame.anchor.y * canvas.height,
    groundOffset: 0,
    source: "motion",
  };
}

export function spriteFromCharacter(state: CharacterState | null | undefined): SpriteSpec | null {
  const asset = state?.active?.assets.find((a) => a.pose === "neutral_full" && a.has_alpha);
  if (!asset) return null;
  return {
    url: asset.url,
    width: asset.width,
    height: asset.height,
    contentHeight: asset.content_box.bottom - asset.content_box.top,
    anchorX: asset.anchor.x * asset.width,
    anchorY: asset.anchor.y * asset.height,
    groundOffset: 0.8,
    source: "character",
  };
}

/** 物种插画没有实测的主体范围：按整张图、脚底在底边中点算。插画尺寸以加载后的实际像素为准，这里先给 1×1 占位。 */
export function spriteFromSpecies(species: PetSpecies | null | undefined): SpriteSpec | null {
  if (!species) return null;
  return { url: speciesArtUrl(species), width: 1, height: 1, contentHeight: 1, anchorX: 0.5, anchorY: 0.97, groundOffset: 0.8, source: "species" };
}

/** 图片加载好以后，按实际像素补上没有实测数据的那种（物种插画）。 */
export function withNaturalSize(spec: SpriteSpec, naturalWidth: number, naturalHeight: number): SpriteSpec {
  if (spec.source !== "species" || !(naturalWidth > 0 && naturalHeight > 0)) return spec;
  return { ...spec, width: naturalWidth, height: naturalHeight, contentHeight: naturalHeight * 0.9, anchorX: naturalWidth * 0.5, anchorY: naturalHeight * 0.97 };
}

/** 在场地坐标里画这张图的位置与大小。 */
export function spritePlacement(spec: SpriteSpec, pet: { x: number; y: number; radius: number }): { x: number; y: number; width: number; height: number } {
  const scale = (pet.radius * 2 * SPRITE_HEIGHT_PER_DIAMETER) / spec.contentHeight;
  const groundY = pet.y + pet.radius * spec.groundOffset;
  return { x: pet.x - spec.anchorX * scale, y: groundY - spec.anchorY * scale, width: spec.width * scale, height: spec.height * scale };
}
